<script lang="ts">
	import { getContext, setContext } from "svelte";
	import { writable, type Writable } from "svelte/store";
	import { RenderMode } from "#gen/bundles/v1/bundle_pb.js";
	import { AssetType, type AssetRef, type Module, type Suite } from "#gen/bundles/v1/bundle_pb.js";
	import type { AssetLoad } from "#lib/api/bundles.js";
	import ModuleButton from "#lib/components/flow/viewer/desktop/ModuleButton.svelte";
	import ModuleImage from "#lib/components/flow/viewer/desktop/ModuleImage.svelte";
	import ModuleInline from "#lib/components/flow/viewer/desktop/ModuleInline.svelte";
	import ModuleThumbnail from "#lib/components/flow/viewer/desktop/ModuleThumbnail.svelte";
	import { isLegacyLayout } from "#lib/components/flow/viewer/layout/types.js";
	import type { ModuleProps } from "#lib/components/flow/viewer/types.js";
	import { isPassthroughType, resolvedModuleNode } from "#lib/components/flow/viewer/utils.js";
	import { qualifyKey, type BundleDag, type BundleNode } from "#lib/dag.js";
	import { createResolutionsStore, navigationTarget, type ResolutionsStore } from "#lib/passthrough.js";

	const dag: Writable<BundleDag<AssetRef>> = getContext("dag");
	const suite: Writable<Suite> = getContext("suite");
	const assets: Writable<Record<string, AssetLoad>> = getContext("assets");
	const modules: Writable<Record<string, Module>> = getContext("modules");
	const passthroughResolutions: ResolutionsStore = getContext<ResolutionsStore | undefined>("passthroughResolutions") ?? createResolutionsStore();

	const { module, metadata, readOnly = false, interpolate = false, layoutName, actionEnabled = true, mode, classes, themeStyle, isLightBackground, isConversionModule, onExecute, onCancel, onShow, onEdit }: ModuleProps = $props();

	const assetRef: Writable<AssetRef> = writable();
	const moduleKey = $derived(deriveModuleKey(module));

	$effect(() => assetRef.set(module.ref));

	function deriveModuleKey(module: BundleNode<AssetRef>): string {
		if ($assets && $assets[module.key] && $assets[module.key].assetType() !== AssetType.MODULE) {
			return module.key;
		} else if ($modules && $modules[module.key]) {
			return qualifyKey($modules[module.key].entryPoint, module.key);
		} else {
			return module.key;
		}
	}

	const passthrough = $derived(isPassthroughType(module.ref?.type));
	const appearanceNode = $derived(passthrough ? (resolvedModuleNode($dag, module, $passthroughResolutions.map[moduleKey]) ?? module) : module);
	const appearanceKey = $derived(deriveModuleKey(appearanceNode));

	const navTarget = $derived.by(() => {
		const key = passthrough ? navigationTarget($passthroughResolutions.map, moduleKey) : moduleKey;
		const node = $dag.getNode(key);
		if (!node) {
			return module;
		}
		return $dag.getNode(deriveModuleKey(node)) ?? node;
	});

	function handleShow(module: BundleNode<AssetRef>) {
		if (module) {
			onShow?.(module);
		}
	}

	setContext("assetRef", assetRef);
</script>

{#snippet renderButton(key: string)}
	{@const module = $dag.getNode(key)}
	{#if module}
		<ModuleButton {suite} {module} {metadata} {layoutName} {mode} {themeStyle} {isLightBackground} {classes} {isConversionModule} {actionEnabled} {interpolate} onShow={() => handleShow(navTarget)} {onEdit} {readOnly} />
	{/if}
{/snippet}

{#snippet renderThumbnail(key: string)}
	{@const module = $dag.getNode(key)}
	{#if module}
		<ModuleThumbnail {suite} {module} {metadata} {layoutName} {mode} {themeStyle} {isLightBackground} {classes} {isConversionModule} {actionEnabled} {interpolate} onShow={() => handleShow(navTarget)} {onEdit} {readOnly} />
	{/if}
{/snippet}

{#snippet renderImage(key: string)}
	{@const module = $dag.getNode(key)}
	{#if module}
		<ModuleImage {suite} {module} {metadata} {layoutName} {mode} {themeStyle} {isLightBackground} {classes} {isConversionModule} {actionEnabled} {interpolate} onShow={() => handleShow(navTarget)} {onEdit} {readOnly} />
	{/if}
{/snippet}

{#snippet renderInline(key: string)}
	{@const module = $dag.getNode(key)}
	{#if module}
		<ModuleInline {suite} {module} {metadata} {themeStyle} {isLightBackground} {mode} {classes} {readOnly} {interpolate} {onEdit} {onExecute} {onCancel} onShow={() => handleShow(navTarget)} />
	{/if}
{/snippet}

{#snippet renderContent(key: string, ref: AssetRef | undefined)}
	{@const renderMode = ref?.renderMode ?? RenderMode.MODAL}
	{#if renderMode === RenderMode.INLINE && $assets[key]}
		{@render renderInline(key)}
	{:else if renderMode === RenderMode.THUMBNAIL}
		{@render renderThumbnail(key)}
	{:else if renderMode === RenderMode.IMAGE}
		{@render renderImage(key)}
	{:else}
		{@render renderButton(key)}
	{/if}
{/snippet}

{#if isConversionModule && isLegacyLayout(layoutName)}
	{@render renderButton(moduleKey)}
{:else}
	{@render renderContent(appearanceKey, appearanceNode.ref)}
{/if}
